// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { AboutYouSectionContent, ProposalSection } from '../../../types/proposal';
import { ADDABLE_PROPOSAL_SECTION_KEYS } from '../../../types/proposal';
import { SectionAboutYou } from './sections/SectionAboutYou';
import { SectionCashflowImpact } from './sections/SectionCashflowImpact';
import { SectionNeedsObjectives } from './sections/SectionNeedsObjectives';
// Imported from the (now-generic) `SectionPensionRecommendation` module path
// rather than `SectionQuoteRecommendation` directly, on purpose: it's the
// thin backward-compatible re-export, and existing tests that mock this
// module path keep working unchanged regardless of which proposal type is
// active.
import { SectionPensionRecommendation as SectionQuoteRecommendation } from './sections/SectionPensionRecommendation';
import { SectionRichText } from './sections/SectionRichText';

/** Section keys that may be removed once added — mirrors the addable set. */
export const OPTIONAL_SECTION_KEYS: ReadonlySet<string> = new Set(ADDABLE_PROPOSAL_SECTION_KEYS);

/**
 * Rich-text SoS sections that support "Generate with AI" (drafted by the
 * SOS_BUILDER use case). DECLARATIONS is intentionally excluded — it is fixed
 * legal boilerplate, not advisor-drafted prose.
 */
export const AI_DRAFTABLE_SOS_SECTIONS: ReadonlySet<string> = new Set([
  'SUSTAINABILITY',
  'PRECAUTIONS',
  'NEXT_STEPS',
]);

/**
 * Dispatches one PENSION/FUND_INVESTMENT/PROTECTION proposal section to its
 * typed renderer — shared by {@link QuoteBackedProposalBuilder}'s editable
 * content pane and {@link QuoteBackedClientPreview}'s read-only document
 * (mirrors {@link renderBreakthroughSection}'s "Advisor view <-> Client view"
 * toggle pattern).
 *
 * Each section component owns its own `readOnly` branch: the parts that are
 * already read-only in the editable builder (facts cards, tables, server-owned
 * figures) render unchanged either way; the editable inputs/pickers/Save
 * buttons render only when `readOnly` is false. `onOpenQuoteOptions` is only
 * ever supplied by the editable builder — the Client view never links back to
 * the advisor-only Quote Options step.
 */
export function renderQuoteBackedSection(
  section: ProposalSection,
  proposalId: string,
  profileId: string | undefined,
  onSaved: (updated: ProposalSection) => void,
  onRemoved: (sectionKey: string) => void,
  onOpenQuoteOptions: (() => void) | undefined,
  recommendationSectionKey: string,
  readOnly = false,
) {
  switch (section.sectionKey) {
    case 'ABOUT_YOU':
      return <SectionAboutYou content={section.content as AboutYouSectionContent} />;
    case 'NEEDS_OBJECTIVES':
      return (
        <SectionNeedsObjectives
          proposalId={proposalId}
          profileId={profileId}
          section={section}
          onSaved={onSaved}
          readOnly={readOnly}
        />
      );
    case recommendationSectionKey:
      return (
        <SectionQuoteRecommendation
          proposalId={proposalId}
          profileId={profileId}
          section={section}
          onSaved={onSaved}
          onOpenQuoteOptions={onOpenQuoteOptions}
          readOnly={readOnly}
        />
      );
    case 'CASHFLOW_IMPACT':
      return (
        <SectionCashflowImpact
          proposalId={proposalId}
          profileId={profileId}
          section={section}
          onSaved={onSaved}
          readOnly={readOnly}
        />
      );
    default:
      return (
        <SectionRichText
          proposalId={proposalId}
          profileId={profileId}
          section={section}
          removable={OPTIONAL_SECTION_KEYS.has(section.sectionKey)}
          onSaved={onSaved}
          onRemoved={onRemoved}
          aiSectionLabel={
            AI_DRAFTABLE_SOS_SECTIONS.has(section.sectionKey) ? section.title : undefined
          }
          aiUseCase="SOS_BUILDER"
          readOnly={readOnly}
        />
      );
  }
}
